import { useCallback, useEffect, useRef, useState } from "react";
import { RememberedFiltersPill } from "@/components/RememberedFiltersPill";
import { NavLink, useNavigate } from "react-router-dom";
import { GlobalSearchResults } from "@/components/GlobalSearchResults";
import {
  LayoutDashboard, Building2,
  ScrollText, Settings, Search, LogOut, ChevronDown, Shield,
  BarChart3, Boxes, Handshake, Contact, Sparkles, GitCommit, CircleUser, VenetianMask, Palette,
  AlertTriangle, X, FileBarChart,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { RoleBadge } from "@/components/Badges";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SecurityBadge } from "@/components/SecurityBadge";
import StatusBar from "@/components/StatusBar";
import SecurityDialog from "@/components/SecurityDialog";
import { ViewAsDialog } from "@/components/ViewAsDialog";
import { toast } from "sonner";
import { CelebrationDatesGate } from "@/components/CelebrationDatesGate";
import { ForcedPasswordChangeDialog } from "@/components/ForcedPasswordChangeDialog";
import MasqueradeBanner from "@/components/MasqueradeBanner";
import ClaudeFab from "@/components/ClaudeFab";
import { WhoIsInDrawer } from "@/components/WhoIsInDrawer";
import NotificationBell from "@/components/NotificationBell";
import { BrandLogo } from "@/components/BrandLogo";
import { OnboardingHost, useOnboarding } from "@/components/OnboardingHost";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AutoHowToButton } from "@/components/HowToButton";
import { BreadcrumbProvider } from "@/context/BreadcrumbContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", testid: "nav-dashboard" },
  { to: "/partners", label: "Partners", icon: Handshake, perm: "partners.view", testid: "nav-partners" },
  { to: "/customers", label: "Customers", icon: Contact, perm: "customers.view", testid: "nav-customers" },
  // Iter 111 — `newUntil` shows a small red "NEW" pill beside the
  // label until the given UTC date; the pill self-hides after that
  // date without any admin action.
  // Iter 115h — the tab needs the page grant AND the underlying data grant
  // (`/analytics/*` is served under activity_logs.view); without it the page
  // could only ever render "Unable to load analytics".
  { to: "/analytics", label: "Analytics", icon: BarChart3, perm: "analytics.view", anyPerm: ["activity_logs.view"], testid: "nav-analytics", newUntil: "2026-10-15T23:59:59Z" },
  // Iter 106c / 115h — Activity page hosts the audit-log stream (needs
  // activity_logs.view) AND the System Logs panel (needs system_logs.view).
  // Requires `activity.view` plus at least one of the two data grants.
  { to: "/activity", label: "Activity", icon: ScrollText, perm: "activity.view", anyPerm: ["activity_logs.view", "system_logs.view"], testid: "nav-activity", newUntil: "2026-10-15T23:59:59Z" },
  // Reports hub — super admins by default; other roles via `reports.view`.
  { to: "/reports", label: "Reports", icon: FileBarChart, perm: "reports.view", testid: "nav-reports", newUntil: "2026-12-31T23:59:59Z" },
  // Iter 111 — "Ask AI" hidden from partners and staff (super-admin
  // only) pending a Phase-2 revisit. Backed by `superOnly` gate below.
  { to: "/ask-claude", label: "Ask AI", icon: Sparkles, perm: "apps.view", testid: "nav-ask-claude", appRoute: "/ask-claude", superOnly: true },
  // Iter 91ak — Changelog page. Visible to anyone who can see apps.
  { to: "/changelog", label: "Changelog", icon: GitCommit, perm: "changelog.view", testid: "nav-changelog" },
  { to: "/settings", label: "Settings", icon: Settings, perm: "settings_*.view", testid: "nav-settings" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  // Iter 110ab — Close the dropdown when the user clicks anywhere
  // outside the search widget (or presses Escape). Previously the
  // dropdown stayed pinned until a result was picked, which felt
  // sticky when the user just wanted to abandon the search.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch {
        setResults(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    setOpen(false);
    setQ("");
    navigate(path);
  };

  // Iter 110aa — Route each search hit to the RIGHT page/deep-link
  // instead of dumping every hit onto its list index:
  //   user   → partner's directory OR customer's OR staff Users tab
  //            depending on the user's role
  //   org    → /partners or /customers based on `type`
  //   app    → /apps/<slug|app_id> (AppViewer)
  // Also seeds the destination's search box with the hit's name so the
  // exact row shows up highlighted after the jump.
  const goToUser = (u) => {
    // Iter 110ad — Route users by their role bucket:
    //   partner  → /partners?q=<organization_name>  (Partners directory)
    //   customer → /customers?q=<organization_name> (Customers directory)
    //   staff    → /settings?tab=staff&q=<email>    (Admin & Staff)
    // For partner/customer users we use `organization_name` because
    // the Partners/Customers list filters by ORG name, not by user
    // email — passing an email there would show zero orgs. If the
    // user has no org (edge case), fall back to email.
    const role  = (u.role || "").toLowerCase();
    const email = u.email || u.name || "";
    // Iter 110ae — Search API returns the org name under `company`
    // (not `organization_name`), so read both to be safe.
    const org   = (u.organization_name || u.company || "").trim();
    if (role.startsWith("partner")) {
      go(`/partners?q=${encodeURIComponent(org || email)}`);
    } else if (role.includes("customer")) {
      go(`/customers?q=${encodeURIComponent(org || email)}`);
    } else {
      go(`/settings?tab=staff&q=${encodeURIComponent(email)}`);
    }
  };
  const goToOrg = (o) => {
    const q = encodeURIComponent(o.name || "");
    if ((o.type || "").toLowerCase() === "partner") go(`/partners?q=${q}`);
    else go(`/customers?q=${q}`);
  };
  const goToApp = (a) => {
    go(`/apps/${a.slug || a.app_id}`);
  };
  const pick = (key, it) => (key === "apps" ? goToApp(it) : key.endsWith("_users") ? goToUser(it) : goToOrg(it));

  return (
    <div className="relative hidden md:block w-72" ref={wrapRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        placeholder="Search users, orgs, apps…"
        className="pl-9 h-9 bg-slate-50 border-slate-200"
      />
      {open && results && <GlobalSearchResults results={results} q={q} onPick={pick} onAll={go} />}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, has } = useAuth();
  const { brand, apply } = useBranding();
  const [securityOpen, setSecurityOpen] = useState(false);
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const baseRole = user?.base_role || user?.role;
  const [claudeAllowed, setClaudeAllowed] = useState(false);
  const items = NAV.filter((n) => {
    // Iter 106c — Support either a single `perm` or a list of
    // alternatives via `anyPerm`. A nav item is visible when the
    // operator holds any listed perm (OR semantics).
    // Iter 106c / 115h — `perm` and `anyPerm` compose with AND: `perm` is the
    // page grant, `anyPerm` the set of data grants where any one is enough.
    const permOk = (n.perm ? has(n.perm) : true)
      && (n.anyPerm?.length ? n.anyPerm.some((p) => has(p)) : true);
    // Iter 111 — `superOnly` gates an item to super_admin. Used to
    // temporarily hide "Ask AI" from partners + staff pending Phase 2.
    const superOk = !n.superOnly || (user?.role === "super_admin" || baseRole === "super_admin");
    return permOk && superOk && (!n.staffOnly || baseRole === "staff") && (!n.appRoute || claudeAllowed);
  }).map((n) => (
    // Iter 111 — Partner-role viewers see "Staff" instead of "Partners"
    // because their /partners page shows their own team, not the
    // Conduit partner directory. Everything else stays the same.
    n.to === "/partners" && baseRole === "partner"
      ? { ...n, label: "Staff" }
      : n
  ));
  return (
    <OnboardingHost user={user}>
      <LayoutInner user={user} logout={logout} apply={apply} brand={brand}
                   securityOpen={securityOpen} setSecurityOpen={setSecurityOpen}
                   initials={initials} baseRole={baseRole}
                   claudeAllowed={claudeAllowed} setClaudeAllowed={setClaudeAllowed}
                   items={items} has={has}>
        {children}
      </LayoutInner>
    </OnboardingHost>
  );
}

// Iter 119ab — Startup health guard surface. The backend now boots in a
// degraded mode rather than dying when a bootstrap step fails; this tells the
// only people who can act on it (super admins) exactly which step went down.
function DegradedBootBanner({ isSuper }) {
  const [failed, setFailed] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/health/startup")
      .then((r) => setFailed(r.data?.failures || []))
      .catch(() => setFailed([]));
  }, [isSuper]);

  if (!isSuper || dismissed || failed.length === 0) return null;
  return (
    <div data-testid="degraded-boot-banner"
      className="mb-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-amber-900">
          Running in degraded mode — {failed.length} startup step{failed.length === 1 ? "" : "s"} failed
        </div>
        <ul className="mt-1 space-y-0.5">
          {failed.map((f) => (
            <li key={f.step} className="truncate text-xs text-amber-800">
              <span className="font-medium">{f.step}</span> — {f.error}
            </li>
          ))}
        </ul>
        <div className="mt-1 text-[11px] text-amber-700">
          Everything else is serving normally. Check System Logs for the full trace.
        </div>
      </div>
      <button type="button" data-testid="degraded-boot-dismiss" onClick={() => setDismissed(true)}
        className="shrink-0 text-amber-500 hover:text-amber-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Iter 90o — wrap the actual chrome in an inner component so it can call
// the `useOnboarding()` hook that OnboardingHost provides via context.
function LayoutInner({ user, logout, apply, brand, securityOpen, setSecurityOpen,
                      initials, baseRole, claudeAllowed, setClaudeAllowed,
                      items, has, children }) {
  // The sticky header's real height (top bar + tab bar + banners) as a CSS
  // variable, so sticky side panels and anchor jumps stop under it, not under
  // the top bar alone.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const set = () => document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [masqPins, setMasqPins] = useState([]);
  const canMasquerade = baseRole === "staff" && !user?.masqueraded_by;
  // Iter 119y — Partner Admins manage their own company profile + branding
  // from this menu. Referral / Agent / Master Agent tiers can't own branding
  // (server enforces too), so the Branding entry hides for them.
  const isPartnerAdmin = user?.role === "partner_admin";
  const brandingLockedForMe = ["referral", "agent", "master agent", "master_agent"]
    .includes((user?.organization_partner_type || "").toLowerCase());

  const loadMasqPins = useCallback(() => {
    if (!canMasquerade) { setMasqPins([]); return; }
    api.get("/auth/masquerade/targets")
      .then((r) => setMasqPins(r.data.pins || []))
      .catch(() => setMasqPins([]));
  }, [canMasquerade]);

  useEffect(() => {
    loadMasqPins();
    window.addEventListener("masquerade-pins-changed", loadMasqPins);
    return () => window.removeEventListener("masquerade-pins-changed", loadMasqPins);
  }, [loadMasqPins]);

  const startViewAs = async (targetId) => {
    try {
      await api.post("/auth/masquerade/start", { target_user_id: targetId });
      window.location.href = "/dashboard";
    } catch {
      toast.error("Could not start View as");
    }
  };

  useEffect(() => {
    api.get("/apps")
      .then((r) => setClaudeAllowed((r.data || []).some((a) => a.route === "/ask-claude" && a.status === "active")))
      .catch(() => setClaudeAllowed(false));
  }, [user?.user_id]);

  useEffect(() => {
    if (user?.branding) apply(user.branding);
  }, [user?.branding, apply]);

  useEffect(() => {
    if (user?.mfa_setup_required) setSecurityOpen(true);
  }, [user?.mfa_setup_required]);

  // Iter 265 — nothing else is reachable until a staff member's birthday and
  // start date are on file. Not shown while an admin is masquerading: that
  // would make them fill somebody else's dates.
  if (user?.needs_celebration_dates && !user?.masqueraded_by) {
    return <CelebrationDatesGate />;
  }

  return (
    <div className="min-h-screen bg-background">
      <StatusBar />
      <header ref={headerRef} className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <MasqueradeBanner />
        <div className="mx-auto max-w-[1400px] px-4 md:px-6">
          <div className="flex h-20 items-center justify-between gap-4">
            <NavLink
              to="/dashboard"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="brand-logo-header"
              aria-label="Go to dashboard"
            >
              <BrandLogo className="h-[70px]" />
            </NavLink>

            {/* Iter 122 — the v2.0 release pill was removed from the header. */}

            <div className="flex items-center gap-3">
              <GlobalSearch />
              <WhoIsInDrawer />
              <NotificationBell />
              <ThemeToggle />
              <SecurityBadge />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 hover:bg-slate-50 transition-colors">
                    {/* Iter 115t — uploaded avatar wins over the SSO photo so a
                        fresh upload shows up here immediately (checkAuth runs
                        after the upload and refreshes this user object). */}
                    {(user?.avatar_url || user?.picture) ? (
                      <img src={user.avatar_url || user.picture} alt="" data-testid="user-menu-avatar" className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{initials}</span>
                    )}
                    <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate">{user?.name}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                    <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <RoleBadge role={user?.base_role || user?.role} label={user?.role_label} />
                      {/* Iter 110p — Partner type pill (Agent / Master
                          Agent / Referral / Reseller / Service
                          Provider). Only shown for partner users
                          whose org carries a `partner_type`; staff
                          and customers hide it. Renders next to the
                          role badge so ops can eyeball a partner's
                          commission bucket at a glance from their
                          own account menu. */}
                      {(user?.base_role || user?.role) === "partner" && (user?.organization_partner_type || "").trim() && (
                        <span
                          data-testid="account-partner-type-pill"
                          className="inline-flex items-center rounded-full bg-[#eef2fb] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
                          title={`${user.organization_name || "Partner"} · ${user.organization_partner_type}`}
                        >
                          {user.organization_partner_type}
                        </span>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Iter 108t — Route by role:
                      • Staff (base_role="staff") → /humi/profile so the
                        HR-portal chrome (Humi header + sidebar) frames
                        their profile edit page — UnitedCloud's canonical
                        HR surface.
                      • Partner + customer → /profile (Conduit-owned,
                        zero vendor branding). Same editable fields
                        (name, phone, birthday, avatar, password),
                        just without any Humi/HR framing. */}
                  {/* Iter 114v — Profile gated by `profile.view`. */}
                  {has("profile.view") && (
                  <DropdownMenuItem
                    data-testid="account-profile-button"
                    onClick={() => {
                      const isStaff = (user?.base_role || user?.role || "").toLowerCase() === "staff";
                      navigate(isStaff ? "/humi/profile" : "/profile");
                    }}
                  >
                    <CircleUser className="h-4 w-4" /> Profile
                  </DropdownMenuItem>
                  )}
                  {brand?.show_account_security !== false && (
                  <DropdownMenuItem data-testid="account-security-button" onClick={() => setSecurityOpen(true)}>
                    <Shield className="h-4 w-4" /> Account security
                    {user?.mfa_enabled && <span className="ml-auto text-[10px] font-semibold text-emerald-600">2FA ON</span>}
                  </DropdownMenuItem>
                  )}
                  {has("onboarding.view") && brand?.show_replay_tour !== false && (
                  <DropdownMenuItem data-testid="account-replay-tour" onClick={() => onboarding.start()}>
                    <Sparkles className="h-4 w-4" /> Replay onboarding tour
                  </DropdownMenuItem>
                  )}
                  {/* Iter 119y — Partner Admin self-service. Their company
                      record and white-label branding live on the Partners
                      page, which renders as a team roster for partner
                      viewers — these deep links open the right dialog. */}
                  {isPartnerAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid="account-company-profile"
                        onClick={() => navigate("/partners?action=edit")}>
                        <Building2 className="h-4 w-4" /> Edit company profile
                      </DropdownMenuItem>
                      {!brandingLockedForMe && (
                        <DropdownMenuItem data-testid="account-company-branding"
                          onClick={() => navigate("/settings?tab=branding")}>
                          <Palette className="h-4 w-4" /> Branding
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  {canMasquerade && (
                    <>
                      <DropdownMenuSeparator />
                      {masqPins.map((p) => (
                        <DropdownMenuItem key={p.user_id} data-testid={`view-as-pin-item-${p.user_id}`}
                          onClick={() => startViewAs(p.user_id)} className="text-amber-700 focus:text-amber-800">
                          <VenetianMask className="h-4 w-4" /> View as {p.name.split(" ")[0]}
                          {p.org_name && <span className="ml-auto max-w-[80px] truncate text-[10px] text-slate-400">{p.org_name}</span>}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuItem data-testid="view-as-open" onClick={() => setViewAsOpen(true)}>
                        <VenetianMask className="h-4 w-4" /> View as…
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-600">
                    <LogOut className="h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <nav className="flex items-center gap-1 overflow-x-auto -mb-px">
            {items.map((n) => {
              const Icon = n.icon;
              // Iter 111 — Red "NEW" pill until `newUntil`. Self-hides
              // after that UTC datetime — no admin flip needed.
              const showNew = !!n.newUntil && Date.now() < new Date(n.newUntil).getTime();
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm transition-colors ${
                      isActive
                        ? "border-primary text-primary font-semibold"
                        : "border-transparent text-slate-600 hover:text-slate-900"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" /> {n.label}
                  {showNew && (
                    <span
                      data-testid={`${n.testid}-new-pill`}
                      className="ml-0.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white leading-none shadow-sm"
                      title="New — introduced this cycle"
                    >
                      New
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8 pb-[200px] md:pb-[200px]">
        <DegradedBootBanner isSuper={user?.base_role === "super_admin" || user?.role === "super_admin"} />
        {/* Iter 102 — Consistent Dashboard › Section › Page breadcrumbs
            rendered by Layout so every protected route gets them
            without touching each page individually. PageHeader no
            longer renders its own copy to avoid duplicates. */}
        <BreadcrumbProvider>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1"><Breadcrumbs /></div>
            <RememberedFiltersPill />
            <AutoHowToButton />
          </div>
          {children}
        </BreadcrumbProvider>
      </main>
      {/* Iter 111 — Ask AI (ClaudeFab + /ask-claude route) hidden
          from partners and staff pending Phase 2 revisit. Only shown
          to super-admins even when the app is otherwise allowed. */}
      {claudeAllowed && (user?.role === "super_admin" || baseRole === "super_admin") && <ClaudeFab />}
      <SecurityDialog open={securityOpen} onOpenChange={setSecurityOpen} forced={user?.mfa_setup_required} />
      {canMasquerade && <ViewAsDialog open={viewAsOpen} onOpenChange={setViewAsOpen} />}
      <ForcedPasswordChangeDialog />
    </div>
  );
}
